import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './LegalPages.css';
import {useAuthStore} from "../../store/useAuthStore";
import {quizService} from "../../api/quizService";
import {useToast} from "../../hooks/useToast";

interface LegalPageProps {
  title: string;
  contentKey: string;
}

const LegalPage: React.FC<LegalPageProps> = ({ title, contentKey }) => {
  const { t } = useTranslation();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  return (
    <div className="legal-page">
      <div
        className={isAuthenticated ? "legal-page__login_content": "legal-page__content"}
        dangerouslySetInnerHTML={{ __html: t(contentKey) }}
      />
    </div>
  );
};

export const TermsPage: React.FC = () => (
  <LegalPage
    title="Terms & Conditions"
    contentKey="termsAndConditions"
  />
);

export const PrivacyPage: React.FC = () => (
  <LegalPage
    title="Privacy Policy"
    contentKey="privacyPolicy"
  />
);

export const SupportPage: React.FC = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    concern: '',
    details: ''
  });
  const { showSuccess } = useToast();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await quizService.sendSupportEmail({
        email: formData.email,
        name: formData.name,
        topic: formData.concern,
        description: formData.details,
      })
      showSuccess(t('supportPage.successMessage'))
      setFormData({
        name: '',
        email: '',
        concern: '',
        details: ''
      })
    } catch (error) {
      console.error('Error sending email:', error);
    }
  };

  return (
    <div className="support-page">
      <div className="contact-form-container">
        <form className="contact-form" onSubmit={handleSubmit}>
          <h1 className="contact-form-title">{t('supportPage.title')}</h1>

          <div className="form-group">
            <label htmlFor="name" className="form-label">{t('supportPage.nameLabel')}</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder={t('supportPage.namePlaceholder')}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="email" className="form-label">
              {t('supportPage.emailLabel')} <span className="required">{t('supportPage.required')}</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder={t('supportPage.emailPlaceholder')}
              className="form-input"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="concern" className="form-label">
              {t('supportPage.concernLabel')} <span className="required">{t('supportPage.required')}</span>
            </label>
            <input
              type="text"
              id="concern"
              name="concern"
              value={formData.concern}
              onChange={handleInputChange}
              placeholder={t('supportPage.concernPlaceholder')}
              className="form-input"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="details" className="form-label">
              {t('supportPage.detailsLabel')} <span className="required">{t('supportPage.required')}</span>
            </label>
            <textarea
              id="details"
              name="details"
              value={formData.details}
              onChange={handleInputChange}
              placeholder={t('supportPage.detailsPlaceholder')}
              className="form-textarea"
              rows={4}
              required
            />
          </div>

          <button type="submit" className="submit-button">
            {t('supportPage.submitButton')}
          </button>
        </form>
      </div>
    </div>
  );
};

export const SubscriptionTermsPage: React.FC = () => (
  <LegalPage
    title="Subscription Terms"
    contentKey="subscriptionTerms"
  />
);
